import { useQuery } from '@tanstack/react-query'
import {
  BoardDetailDataType,
  getBoardDetail,
  getBoardList,
} from '@tmap-web-lib/remote-api-client/frontman'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { QUERY_KEY } from '@/constants/key'
import {
  hideNoticeToday,
  isNoticeHiddenToday,
  isNoticeShownInSession,
  markNoticeShownInSession,
} from '@/utils/noticeStorage'

const BOARD_CODE = 'BOARD-CX-NOTICE'

function useNoticePopup() {
  const [isClosed, setIsClosed] = useState(false)

  const { data: listData } = useQuery({
    queryKey: [QUERY_KEY.CX_NOTICE, 'list'],
    queryFn: () =>
      getBoardList(true, { boardCode: BOARD_CODE, pageNo: 1, pageSize: 1, boardStatus: '1' }),
    select: data => data as any,
  })

  const latestNotice = useMemo(() => {
    if (!listData?.boards?.length) return null
    return listData.boards[0]
  }, [listData])

  const shouldFetchDetail = useMemo(() => {
    if (!latestNotice) return false
    if (isNoticeShownInSession()) return false
    return !isNoticeHiddenToday(latestNotice.boardKey)
  }, [latestNotice])

  const { data: detailData } = useQuery({
    queryKey: [QUERY_KEY.CX_NOTICE, 'detail', latestNotice?.boardKey],
    queryFn: () =>
      getBoardDetail(true, { boardCode: BOARD_CODE, boardKey: latestNotice!.boardKey }),
    select: data => data as any as BoardDetailDataType,
    enabled: shouldFetchDetail,
  })

  const isOpen = shouldFetchDetail && !!detailData && !isClosed

  useEffect(() => {
    if (isOpen) {
      return markNoticeShownInSession
    }
  }, [isOpen])

  const handleClose = useCallback(() => {
    setIsClosed(true)
  }, [])

  const handleHideToday = useCallback(() => {
    if (latestNotice) {
      hideNoticeToday(latestNotice.boardKey)
    }
    setIsClosed(true)
  }, [latestNotice])

  return {
    isOpen,
    noticeDetail: detailData ?? null,
    handleClose,
    handleHideToday,
  }
}

export default useNoticePopup
